import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  useGetV1SitesIdEffectiveConfig,
  type EnvelopeEffectiveConfigDataParametersItem as Param,
  type Figure,
} from "@solarkey/api-client";
import { ErrorState, Skeleton, Term, atDisplayScale } from "@solarkey/ui";

import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import { useHasLens } from "../session/capabilities";
import type { Lens } from "../session/types";

/* ── Screen 13 (P11): Site settings, read ──
   Read-only on the site. Every row comes from GET /v1/sites/{id}/effective-config
   (directive §5.7, Envelope_EffectiveConfig) and shows the LAYER it resolved from
   plus the effective-from date of the row that supplied it. Nothing is typed into
   this screen: a configuration fact no table holds is absent from `parameters`, so
   the card states the gap in words instead of showing a plausible value.

   This screen used to render a fixture — a fictional estate, a fictional tariff and
   invented contacts, shown under whichever real site you opened. The endpoint it
   claimed did not exist has been in the frozen contract all along. */

type Group = {
  key: string;
  title: string;
  sub?: string;
  lens?: Lens;
  /** Which resolved parameter keys belong on this card. */
  match: (key: string) => boolean;
  /** Facts the wireframe asks for that NO table holds, and the true reason why.
      `onlyIfEmpty` gaps describe the card's own table, so they are dropped once
      the server does return rows for it — a gap claim must not outlive the gap. */
  gaps?: { label: string; reason: string; onlyIfEmpty?: boolean }[];
};

const startsWithAny = (key: string, prefixes: string[]): boolean =>
  prefixes.some((p) => key.startsWith(p));

// The six cards of wireframe §13, in its row-major order (baseline · tariff ·
// grid // thresholds · contacts · data sources). Each still draws only the rows
// the §5.7 resolver actually returns; a fact no table holds is a "Not held" row
// with the true reason, never an invented value.
const GROUPS: Group[] = [
  {
    key: "baseline",
    title: "Commissioning baseline",
    sub: "registry",
    // Rendered by BaselineCard, which COMPOSES the wireframe's rows from these
    // keys (capacity DC·AC on one line, inverters as count × rating, etc.). The
    // designated instruments fold in here; the per-class device counts go to the
    // Data sources card, as the wireframe's "Devices" row.
    match: (k) =>
      [
        "dc_capacity_kwp",
        "ac_capacity_kw",
        "commissioning_date",
        "tilt_degrees",
        "azimuth_degrees",
        "device_count_inverter",
        "inverter_rated_kw_total",
        "designated_poa_sensor_key",
      ].includes(k),
  },
  {
    key: "tariff",
    title: "Tariff and value basis",
    sub: "Value lens",
    lens: "value",
    match: (k) =>
      startsWithAny(k, ["tariff_", "demand_charge"]) ||
      ["currency", "emission_factor_kg_per_kwh", "financial_year_start"].includes(k),
    gaps: [
      {
        label: "Value basis and budget table",
        reason: "no budget/plan table is loaded for this site, and no value-basis column exists",
      },
    ],
  },
  {
    key: "grid",
    title: "Grid limits and ratings",
    match: (k) => k.startsWith("grid_") || k.startsWith("transformer_"),
    gaps: [
      {
        label: "Export limit, ramp rate, must-run, grid instruction channel",
        reason: "no interconnection-terms table exists; these are not held anywhere yet",
      },
      {
        label: "Deep-curtailment confirmation threshold",
        reason: "no tenant policy table is loaded",
      },
    ],
  },
  {
    key: "thresholds",
    title: "Thresholds",
    match: () => false,
    gaps: [
      {
        label: "Coverage floor, offline-gap, transformer OTI, under-performance, soiling, data-path-stale",
        reason: "no threshold table is loaded; each detection rule carries its own constant until one is",
      },
    ],
  },
  {
    key: "contacts",
    title: "Contacts",
    sub: "People lens",
    lens: "people",
    match: () => false,
    gaps: [
      {
        label: "Site manager, contractor, grid control room, OEM desk, escalation",
        reason: "no contacts or party table exists in the schema — nothing to resolve a name or a reach from",
      },
    ],
  },
  {
    key: "datasources",
    title: "Data sources",
    // The platform/data-path keys, plus any metering constants (the wireframe
    // has no separate metering card).
    match: (k) =>
      ["logger_id", "data_interval_minutes"].includes(k) ||
      k.startsWith("device_count_"),
    gaps: [
      {
        label: "Site SCADA, cloud-ingest and engine rows",
        reason: "hop inventory and cadences are not registered; the live data path is on Site: Now instead",
      },
    ],
  },
];

/** Human labels for the keys the resolver emits. Unlisted keys are humanised. */
const LABELS: Record<string, string> = {
  ac_capacity_kw: "Capacity, AC",
  asset_class: "Asset class",
  azimuth_degrees: "Azimuth",
  commissioning_date: "Commercial operation",
  currency: "Currency",
  data_interval_minutes: "Data interval",
  dc_capacity_kwp: "Capacity, DC",
  designated_energy_basis: "Designated energy basis",
  designated_module_gamma_per_c: "Module temperature coefficient",
  designated_module_temp_sensor_key: "Designated module-temperature sensor",
  designated_poa_sensor_key: "Designated POA sensor",
  emission_factor_kg_per_kwh: "Emission factor",
  financial_year_start: "Financial year starts",
  locale: "Locale and grouping",
  logger_id: "Logger",
  nominal_frequency_hz: "Nominal frequency",
  over_frequency_hz: "Over-frequency limit",
  region_pack_code: "Region pack",
  tilt_degrees: "Tilt",
  timezone: "Time zone",
  timezone_tenant_default: "Time zone, tenant default",
  under_frequency_hz: "Under-frequency limit",
};

const sentence = (text: string): string =>
  text.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/** Keys the resolver composes at runtime (per class, per device) get a shape,
    not a hardcoded name. The unit is never part of the label — it travels with
    the value from the API (UX-003 §6.2). */
function humanise(key: string): string {
  const label = LABELS[key];
  if (label) return label;
  if (key.startsWith("device_count_")) {
    return `${sentence(key.slice("device_count_".length))} count`;
  }
  const ratedTotal = /^(.+)_rated_kw_total$/.exec(key);
  if (ratedTotal) return `${sentence(ratedTotal[1]!)} rated total`;
  return sentence(key);
}

function formatValue(p: Param): string {
  const raw = p.value;
  let text: string;
  if (typeof raw === "number") {
    text = raw.toLocaleString(undefined, { maximumFractionDigits: 4 });
  } else if (typeof raw === "boolean") {
    text = raw ? "yes" : "no";
  } else if (typeof raw === "string") {
    text = raw;
  } else {
    text = JSON.stringify(raw);
  }
  // The unit travels WITH the value from the API (it may carry a currency read
  // from the tariff row). It is never composed from a literal in this file.
  return p.unit ? `${text} ${p.unit}` : text;
}

/** "site · from 2026-09-01 to 2027-03-31" — the layer plus its effective window. */
function formatFrom(p: Param): string {
  const parts = [p.source_layer ?? "unresolved"];
  if (p.effective_from) parts.push(`from ${p.effective_from}`);
  if (p.effective_to) parts.push(`to ${p.effective_to}`);
  return parts.join(" · ");
}

export function SiteSettings(
  { site, tenant, scope }: { site: string; tenant?: string; scope?: string },
): ReactNode {
  const navigate = useNavigate();
  const hasValueLens = useHasLens("value");
  const hasPeopleLens = useHasLens("people");
  const canEdit = !!(tenant && scope);
  const query = useGetV1SitesIdEffectiveConfig(site, undefined);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-[14px]" aria-busy="true">
        <Skeleton variant="text" width="16rem" />
        <div className="sk-set-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="tile" />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Settings unavailable"
        message="The resolved configuration for this site could not be read. Nothing is shown rather than a stale or assumed value."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const envelope = query.data.data;
  const params = (envelope.data?.parameters ?? []).filter((p) => p.key);
  const packCode = params.find((p) => p.key === "region_pack_code");
  const lensAllows = (lens?: Lens): boolean =>
    lens === undefined ||
    (lens === "value" ? hasValueLens : lens === "people" ? hasPeopleLens : true);

  // Cards the person's lenses permit. A card the lens withholds is ABSENT and the
  // grid closes up (UX-001 §4: the UI renders what the server allows).
  const visible = GROUPS.filter((g) => lensAllows(g.lens));
  // First card to match a key owns it, so a key never renders on two cards.
  const claimed = new Set<string>();
  const cards = visible.map((group) => {
    const rows = params.filter((p) => !claimed.has(p.key!) && group.match(p.key!));
    rows.forEach((p) => claimed.add(p.key!));
    return { group, rows };
  });

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        title={`Site ${site} · Settings`}
        scope="As configured · read only here · resolved from ruling, tenant, region pack, site and contract"
        timeControl="none"
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {packCode ? (
              <span className="sk-basis-chip">{`pack ${String(packCode.value)}`}</span>
            ) : null}
            {envelope.meta?.rule_version ? (
              <span className="sk-basis-chip">{envelope.meta.rule_version}</span>
            ) : null}
            <button
              type="button"
              style={editBtnStyle}
              onClick={() => { if (canEdit) void navigate({ to: `/t/${tenant}/s/${scope}/admin` }); }}
            >
              Edit in Admin
            </button>
          </div>
        }
      />
      <SiteTabBar site={site} />

      <div className="sk-set-grid">
        {cards.map(({ group, rows }) =>
          group.key === "baseline"
            ? <BaselineCard key={group.key} rows={rows} />
            : group.key === "tariff"
              ? <TariffCard key={group.key} rows={rows} />
              : group.key === "grid"
                ? <GridCard key={group.key} rows={rows} />
                : group.key === "thresholds"
                  ? <ThresholdsCard key={group.key} />
                  : group.key === "datasources"
                    ? <DataSourcesCard key={group.key} rows={rows} />
                    : group.key === "contacts"
                      ? <ContactsCard key={group.key} />
                      : <SettingsCard key={group.key} group={group} rows={rows} />,
        )}
      </div>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtIsoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** A config Param rendered through Metric, so its unit (and MW/MWp scaling)
 *  comes from the figure, never a hardcoded literal (UX-003). */
function toFig(p: Param, valueOverride?: number): Figure {
  return { value: valueOverride ?? (p.value as number), unit: p.unit ?? "", state: "MEASURED" } as Figure;
}
/** A figure as plain inline text at the table's own size — the unit comes from
 *  the (optionally display-scaled) figure, never a literal, so it reads e.g.
 *  "42 MWp" / "1,000 kW" without the tile-sized Metric styling. */
function metric(fig: Figure, scale: boolean, maxFrac = 1): string {
  const f = scale ? (atDisplayScale(fig) ?? fig) : fig;
  const v = typeof f.value === "number" ? f.value.toLocaleString(undefined, { maximumFractionDigits: maxFrac }) : "";
  return f.unit ? `${v} ${f.unit}` : v;
}
function joinDot(nodes: ReactNode[]): ReactNode {
  return nodes.flatMap((n, i) => (i ? [" · ", n] : [n]));
}
/** A withheld value shown inline in a composed row (never a 0, never invented). */
function notHeldNode(reason: string): ReactNode {
  return <span className="sk-set-note" style={{ display: "inline" }} title={reason}>Not held</span>;
}

/** Commissioning baseline — the wireframe's seven composed rows (§13 card 2):
 *  capacity on one line, inverters as count × rating, the designated bases, and
 *  honest "Not held" where a fact no table carries. Values are still the
 *  resolver's; this only COMPOSES them into the rows the wireframe draws. */
function BaselineCard({ rows: params }: { rows: Param[] }): ReactNode {
  const byKey = new Map(params.map((p) => [p.key!, p] as const));
  const get = (k: string): Param | undefined => byKey.get(k);
  const num = (k: string): number | undefined => {
    const v = get(k)?.value;
    return typeof v === "number" ? v : undefined;
  };
  const notHeld = (reason: string): ReactNode => (
    <span className="sk-set-note" style={{ display: "inline" }} title={reason}>Not held</span>
  );

  // `label` stays a string (it is the React key); `labelNode` optionally renders
  // the label with a specialist term wrapped in <Term> for its glossary tooltip.
  type Row = { label: string; labelNode?: ReactNode; value: ReactNode; from: string };
  const out: Row[] = [];

  // 1 — Commercial operation
  const com = get("commissioning_date");
  out.push({
    label: "Commercial operation",
    value: com && typeof com.value === "string" ? fmtIsoDate(com.value) : notHeld("no commissioning date is registered"),
    from: com ? formatFrom(com) : "",
  });

  // 2 — Capacity (DC · AC; Metric + atDisplayScale give MWp/MW from the figure)
  const dcP = get("dc_capacity_kwp");
  const acP = get("ac_capacity_kw");
  const capNodes: ReactNode[] = [];
  if (typeof dcP?.value === "number") capNodes.push(<span key="dc">{metric(toFig(dcP), true)} DC</span>);
  if (typeof acP?.value === "number") capNodes.push(<span key="ac">{metric(toFig(acP), true)} AC</span>);
  out.push({
    label: "Capacity",
    value: capNodes.length ? joinDot(capNodes) : notHeld("no capacity is registered"),
    from: formatFrom(dcP ?? acP ?? ({ source_layer: "unresolved" } as Param)),
  });

  // 3 — Modules (geometry held; count/Wp not)
  const tilt = num("tilt_degrees");
  const az = num("azimuth_degrees");
  const geo: string[] = [];
  if (tilt != null) geo.push(`fixed tilt ${fmtNum(tilt)}°`);
  if (az != null) geo.push(`azimuth ${fmtNum(az)}°`);
  out.push({
    label: "Modules",
    value: geo.length ? geo.join(" · ") : notHeld("no PV-module catalogue exists — count, Wp, make and model are not held"),
    from: geo.length ? formatFrom(get("tilt_degrees") ?? get("azimuth_degrees")!) : "",
  });

  // 4 — Inverters (count × per-inverter rating; kW kept, as the wireframe shows)
  const icount = num("device_count_inverter");
  const iratedP = get("inverter_rated_kw_total");
  const irated = typeof iratedP?.value === "number" ? iratedP.value : undefined;
  let inverters: ReactNode;
  if (icount != null && icount > 0 && irated != null) {
    inverters = <span>{icount} × {metric(toFig(iratedP!, Math.round(irated / icount)), false, 0)}</span>;
  } else if (icount != null) {
    inverters = `${icount}`;
  } else {
    inverters = notHeld("no inverter count is registered");
  }
  out.push({
    label: "Inverters",
    value: inverters,
    from: icount != null ? formatFrom(get("device_count_inverter")!) : "",
  });

  // 5 — Degradation assumption (not held)
  out.push({
    label: "Degradation assumption",
    value: notHeld("no contract register row carries a degradation rate for this site"),
    from: "",
  });

  // 6 — Reference yield basis (the designated POA instrument)
  const poa = get("designated_poa_sensor_key");
  out.push({
    label: "Reference yield basis",
    value: poa && typeof poa.value === "string" ? `POA · ${poa.value}` : notHeld("no designated POA sensor is set"),
    from: poa ? formatFrom(poa) : "",
  });

  // 7 — PR basis (fixed by the KPI ruling, not per-site)
  out.push({
    label: "PR basis",
    labelNode: <><Term tkey="pr">PR</Term> basis</>,
    value: "DC · ratio of sums",
    from: "ruling",
  });

  return (
    <section aria-labelledby="set-baseline" className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id="set-baseline" className="sk-section__title">Commissioning baseline</h2>
          <span className="sk-section__sub">registry</span>
        </div>
      </div>
      <table className="sk-set-table">
        <thead>
          <tr><th>Key</th><th>Value</th><th>From</th></tr>
        </thead>
        <tbody>
          {out.map((r) => (
            <tr key={r.label}>
              <td>{r.labelNode ?? r.label}</td>
              <td>{r.value}</td>
              <td className="sk-set-from">{r.from}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Tariff and value basis — the wireframe's six composed rows (§13 card 3),
 *  under the Value lens. The tariff rate and emission factor render through
 *  Metric so the currency and unit come from the figure, never a literal
 *  (Rule 3: no literal currency code or symbol in code). Value basis and the
 *  budget table have no table
 *  to resolve from, so they are honest "Not held". */
function TariffCard({ rows: params }: { rows: Param[] }): ReactNode {
  const byKey = new Map(params.map((p) => [p.key!, p] as const));
  const get = (k: string): Param | undefined => byKey.get(k);

  type Row = { label: string; value: ReactNode; from: string };
  const out: Row[] = [];

  // 1 — Value basis (no value-basis column exists)
  out.push({ label: "Value basis", value: notHeldNode("no value-basis column exists in the schema"), from: "" });

  // 2 — Tariff (rate through Metric; its unit carries the currency)
  const rate = get("tariff_rate");
  out.push({
    label: "Tariff",
    value: rate && typeof rate.value === "number"
      ? metric(toFig(rate), false, 2)
      : notHeldNode("no tariff rate is loaded for this site"),
    from: rate ? formatFrom(rate) : "",
  });

  // 3 — Currency (the resolver's value, never a literal)
  const cur = get("currency");
  out.push({
    label: "Currency",
    value: cur ? formatValue(cur) : notHeldNode("no currency is set"),
    from: cur ? formatFrom(cur) : "",
  });

  // 4 — Financial year
  const fy = get("financial_year_start");
  out.push({
    label: "Financial year",
    value: fy ? formatValue(fy) : notHeldNode("no financial-year start is set"),
    from: fy ? formatFrom(fy) : "",
  });

  // 5 — Budget table (no budget/plan table loaded)
  out.push({ label: "Budget table", value: notHeldNode("no budget/plan table is loaded for this site"), from: "" });

  // 6 — Emission factor (through Metric; the effective date travels in From)
  const ef = get("emission_factor_kg_per_kwh");
  out.push({
    label: "Emission factor",
    value: ef && typeof ef.value === "number" ? metric(toFig(ef), false, 3) : notHeldNode("no emission factor is set"),
    from: ef ? formatFrom(ef) : "",
  });

  // Any further value-lens facts the resolver returned (e.g. a demand charge)
  // are appended so nothing is dropped; the tariff currency is already carried
  // by the rate's unit, so it is not repeated.
  const used = new Set(["tariff_rate", "tariff_currency", "currency", "financial_year_start", "emission_factor_kg_per_kwh"]);
  for (const p of params) {
    if (p.key && !used.has(p.key)) out.push({ label: humanise(p.key), value: formatValue(p), from: formatFrom(p) });
  }

  return (
    <section aria-labelledby="set-tariff" className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id="set-tariff" className="sk-section__title">Tariff and value basis</h2>
          <span className="sk-section__sub">Value lens</span>
        </div>
      </div>
      <table className="sk-set-table">
        <thead>
          <tr><th>Key</th><th>Value</th><th>From</th></tr>
        </thead>
        <tbody>
          {out.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td>{r.value}</td>
              <td className="sk-set-from">{r.from}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sk-set-note">Without the Value lens this card is absent and the layout closes up.</p>
    </section>
  );
}

/** Grid limits and ratings — the wireframe's six composed rows (§13 card 4).
 *  Each maps to the resolver key that would carry it; numeric limits render
 *  through Metric (so kW shows as MW), transformer ratings compose across the
 *  transformer rows, and a fact no table holds is an honest inline "Not held".
 *  Any extra grid fact the resolver returned (e.g. frequency band) is appended
 *  so nothing is dropped. */
function GridCard({ rows: params }: { rows: Param[] }): ReactNode {
  const find = (re: RegExp): Param | undefined => params.find((p) => p.key && re.test(p.key));
  const renderVal = (p: Param): ReactNode =>
    typeof p.value === "number" ? metric(toFig(p), true, 1) : formatValue(p);

  type Row = { label: string; value: ReactNode; from: string };
  const out: Row[] = [];
  const used = new Set<string>();
  const row = (label: string, p: Param | undefined, reason: string): void => {
    if (p?.key) used.add(p.key);
    out.push({ label, value: p ? renderVal(p) : notHeldNode(reason), from: p ? formatFrom(p) : "" });
  };

  const NO_INTERCONNECT = "no interconnection-terms table exists — this is not held anywhere yet";

  // 1 — Export limit, 2 — Ramp rate
  row("Export limit", find(/export.*limit/i), NO_INTERCONNECT);
  row("Ramp rate", find(/ramp.*rate/i), NO_INTERCONNECT);
  // 3 — Deep-curtailment threshold (tenant policy)
  row("Deep-curtailment threshold", find(/deep.?curtail|curtailment.*(threshold|confirm)/i), "no tenant policy table is loaded");
  // 4 — Transformer ratings (compose across the transformer rating rows)
  const xf = params.filter((p) => p.key && /transformer/i.test(p.key) && /(rated|rating|mva|kva)/i.test(p.key));
  xf.forEach((p) => used.add(p.key!));
  out.push({
    label: "Transformer ratings",
    value: xf.length ? joinDot(xf.map((p) => <span key={p.key}>{renderVal(p)}</span>)) : notHeldNode("no transformer rating is registered in the asset graph"),
    from: xf.length ? formatFrom(xf[0]!) : "",
  });
  // 5 — Must-run, 6 — Grid instruction channel
  row("Must-run", find(/must.?run/i), NO_INTERCONNECT);
  row("Grid instruction channel", find(/instruction.*channel|grid.*channel/i), "no grid-instruction channel is registered");

  return (
    <section aria-labelledby="set-grid" className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id="set-grid" className="sk-section__title">Grid limits and ratings</h2>
        </div>
      </div>
      <table className="sk-set-table">
        <thead>
          <tr><th>Key</th><th>Value</th><th>From</th></tr>
        </thead>
        <tbody>
          {out.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td>{r.value}</td>
              <td className="sk-set-from">{r.from}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Thresholds — the wireframe's six rows (§13 card 5). No threshold table is
 *  loaded, so each reads an honest "Not held" with the reason; the detection
 *  rules carry their own constants until a table exists. */
function ThresholdsCard(): ReactNode {
  const rows: { label: string; reason: string }[] = [
    { label: "Coverage floor", reason: "no threshold table is loaded; the coverage rule carries its own floor" },
    { label: "Offline gap to Not measured", reason: "no threshold table is loaded; the detection rule carries its own gap" },
    { label: "Transformer OTI alert · trip", reason: "no transformer OTI threshold is registered for this site" },
    { label: "Inverter under-performance", reason: "no threshold table is loaded; the detection rule carries its own band" },
    { label: "Soiling trigger", reason: "no threshold table is loaded" },
    { label: "Data path stale", reason: "no threshold table is loaded; the data-path rule carries its own packet count" },
  ];
  return (
    <section aria-labelledby="set-thresholds" className="sk-section">
      <div className="sk-section__head">
        <div><h2 id="set-thresholds" className="sk-section__title">Thresholds</h2></div>
      </div>
      <table className="sk-set-table">
        <thead><tr><th>Key</th><th>Value</th><th>From</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td>{notHeldNode(r.reason)}</td>
              <td className="sk-set-from"></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Data sources — the wireframe's hop list (§13 card 7): Devices · Logger ·
 *  Site SCADA · Cloud ingest · Engines, as Hop/Source/Cadence. Devices compose
 *  from the per-class counts; the logger from its id; the hops with no registry
 *  are honest "Not held". Platform config the resolver also returned (timezone,
 *  locale, pack) is appended so nothing is dropped. */
function DataSourcesCard({ rows: params }: { rows: Param[] }): ReactNode {
  const byKey = new Map(params.map((p) => [p.key!, p] as const));
  const get = (k: string): Param | undefined => byKey.get(k);

  const counts = params.filter((p) => p.key?.startsWith("device_count_") && typeof p.value === "number" && p.value > 0);
  const devices: ReactNode = counts.length
    ? counts
        .map((p) => `${p.value} ${sentence(p.key!.slice("device_count_".length)).toLowerCase()}${p.value === 1 ? "" : "s"}`)
        .join(" · ")
    : notHeldNode("no device inventory is registered for this site");

  const logger = get("logger_id");
  const interval = get("data_interval_minutes");

  type Row = { hop: string; source: ReactNode; cadence: ReactNode };
  const out: Row[] = [
    { hop: "Devices", source: devices, cadence: "" },
    {
      hop: "Logger",
      source: logger ? formatValue(logger) : notHeldNode("no logger is registered"),
      cadence: interval ? formatValue(interval) : "",
    },
    { hop: "Site SCADA", source: notHeldNode("no SCADA hop is registered — the live data path is on Site: Now"), cadence: "" },
    { hop: "Cloud ingest", source: notHeldNode("the ingest hop is not registered in configuration"), cadence: "" },
    { hop: "Engines", source: notHeldNode("the engine hop is not registered in configuration"), cadence: "" },
  ];

  return (
    <section aria-labelledby="set-datasources" className="sk-section">
      <div className="sk-section__head">
        <div><h2 id="set-datasources" className="sk-section__title">Data sources</h2></div>
      </div>
      <table className="sk-set-table">
        <thead><tr><th>Hop</th><th>Source</th><th>Cadence</th></tr></thead>
        <tbody>
          {out.map((r, i) => (
            <tr key={`${r.hop}-${i}`}>
              <td>{r.hop}</td>
              <td>{r.source}</td>
              <td className="sk-set-from">{r.cadence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Contacts — the wireframe's Role/Who/Reach table (§13 card 6), under the
 *  People lens. No contacts or party table exists in the schema, so every role
 *  is an honest "Not held". */
function ContactsCard(): ReactNode {
  const roles = ["Site manager", "O&M contractor", "Grid control room", "OEM, inverters", "Escalation"];
  const reason = "no contacts or party table exists in the schema — nothing to resolve a name or a reach from";
  return (
    <section aria-labelledby="set-contacts" className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id="set-contacts" className="sk-section__title">Contacts</h2>
          <span className="sk-section__sub">People lens</span>
        </div>
      </div>
      <table className="sk-set-table">
        <thead><tr><th>Role</th><th>Who</th><th>Reach</th></tr></thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r}>
              <td>{r}</td>
              <td>{notHeldNode(reason)}</td>
              <td className="sk-set-from"></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function SettingsCard({ group, rows }: { group: Group; rows: Param[] }): ReactNode {
  const gaps = (group.gaps ?? []).filter((g) => !g.onlyIfEmpty || rows.length === 0);

  return (
    <section aria-labelledby={`set-${group.key}`} className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id={`set-${group.key}`} className="sk-section__title">{group.title}</h2>
          {group.sub ? <span className="sk-section__sub">{group.sub}</span> : null}
        </div>
      </div>

      {rows.length > 0 ? (
        <table className="sk-set-table">
          <thead>
            <tr><th>Key</th><th>Value</th><th>From</th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.key}>
                <td>{humanise(p.key!)}</td>
                <td>{formatValue(p)}</td>
                <td className="sk-set-from" title={p.reason ?? undefined}>{formatFrom(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="sk-set-note">
          {gaps.length > 0
            ? "No value on this card is held for this site yet. The rows below say which facts are missing and why — nothing is assumed in their place."
            : "No value on this card is held for this site yet."}
        </p>
      )}

      {gaps.length > 0 ? (
        <table className="sk-set-table" aria-label={`${group.title} — not held`}>
          <thead>
            <tr><th>Not held</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {gaps.map((gap) => (
              <tr key={gap.label}>
                <td>{gap.label}</td>
                <td className="sk-set-from">{gap.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

const editBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 30,
  padding: "0 12px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
